import { useState } from 'react';
import {
  EmptyState,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  StatusPill,
  type StatusLevel,
} from '@/lib';
import { Gated, useCapability } from '@/app/CapabilitiesProvider';
import type { ApiClients } from '@/api/client';
import {
  useOrg,
  useOrgUsers,
  useInvitations,
  useChangeUserRole,
  useDeactivateUser,
  useActivateUser,
  useInviteUser,
  type OrgUser,
  type Invitation,
} from './queries';
import { useGroups } from '@/screens/S14Groups/queries';

const ROLE_TONE: Record<string, StatusLevel> = {
  admin: 'critical',
  manager: 'warn',
  maintainer: 'watch',
  viewer: 'healthy',
};

const ROLES_BELOW_ADMIN = ['viewer', 'maintainer', 'manager'] as const;

export type S13UsersProps = {
  clients: ApiClients;
};

/**
 * S13 Users. R7. Composes R2a/b/c endpoints:
 *   - GET /v1/orgs/current        header
 *   - GET /v1/users               row table
 *   - GET /v1/invitations         pending invites strip
 *   - PATCH /v1/users/{id}        inline role change
 *   - POST /v1/users/{id}/[de]activate  status toggle
 *   - POST /v1/invitations        invite form
 *
 * Every mutation surface is wrapped in <Gated capability="…"> so a
 * viewer sees the list but no action affordances.
 */
export function S13Users({ clients }: S13UsersProps) {
  const org = useOrg(clients);
  const users = useOrgUsers(clients);
  const invitations = useInvitations(clients);

  const canChangeRole = useCapability('user.change_role');
  const canDeactivate = useCapability('user.deactivate');
  const canPromoteAdmin = useCapability('user.promote_admin');

  const [inviteOpen, setInviteOpen] = useState(false);

  if (users.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load users"
        body={String(users.error)}
        cta={{ label: 'Retry', onClick: () => users.refetch() }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Organization
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
            {org.data?.display_name ?? '…'}
          </h1>
          <p className="mt-sp-1 text-fs-meta text-gray-700">
            {org.data?.user_count ?? '—'} users
          </p>
        </div>
        <Gated capability="user.invite">
          <PrimaryButton
            label={inviteOpen ? 'Cancel invite' : 'Invite user'}
            onClick={() => setInviteOpen((v) => !v)}
          />
        </Gated>
      </header>

      <Gated capability="user.invite">
        {inviteOpen ? (
          <InviteForm
            clients={clients}
            allowAdmin={canPromoteAdmin}
            onDone={() => setInviteOpen(false)}
          />
        ) : null}
      </Gated>

      {invitations.data && invitations.data.length > 0 ? (
        <section aria-labelledby="invites-heading">
          <h2
            id="invites-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Pending invitations
          </h2>
          <ul className="mt-sp-3 flex flex-col gap-sp-2">
            {invitations.data
              .filter((i) => i.status === 'pending')
              .map((invite) => (
                <InvitationRow key={invite.token} invite={invite} />
              ))}
          </ul>
        </section>
      ) : null}

      <section aria-labelledby="users-heading">
        <h2
          id="users-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          Members
        </h2>
        {users.isLoading ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">Loading users…</p>
        ) : !users.data || users.data.length === 0 ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">No users yet.</p>
        ) : (
          <ul className="mt-sp-3 flex flex-col gap-sp-2">
            {users.data.map((u) => (
              <UserRow
                key={u.user_id}
                user={u}
                clients={clients}
                canChangeRole={canChangeRole}
                canDeactivate={canDeactivate}
                canPromoteAdmin={canPromoteAdmin}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function UserRow({
  user,
  clients,
  canChangeRole,
  canDeactivate,
  canPromoteAdmin,
}: {
  user: OrgUser;
  clients: ApiClients;
  canChangeRole: boolean;
  canDeactivate: boolean;
  canPromoteAdmin: boolean;
}) {
  const changeRole = useChangeUserRole(clients);
  const deactivate = useDeactivateUser(clients);
  const activate = useActivateUser(clients);

  const tone = ROLE_TONE[user.normalized_role] ?? 'watch';
  // Always include the user's current role in the option list, even
  // when the caller can't change to that role. Otherwise the select
  // displays a stale value when a manager views an admin row.
  const baseRoles = canPromoteAdmin
    ? [...ROLES_BELOW_ADMIN, 'admin']
    : [...ROLES_BELOW_ADMIN];
  const allowedRoles = baseRoles.includes(user.normalized_role)
    ? baseRoles
    : [...baseRoles, user.normalized_role];
  // Disable the select entirely when the caller can't promote AND
  // the target is an admin — the dropdown would otherwise look
  // editable but every change would 403.
  const selectDisabled =
    !canChangeRole ||
    (user.normalized_role === 'admin' && !canPromoteAdmin) ||
    changeRole.isPending;

  return (
    <li className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2">
      <div className="min-w-0">
        <p className="truncate text-fs-body font-medium text-gray-900">
          {user.display_name || user.email}
        </p>
        <p className="text-fs-meta text-gray-700">{user.email}</p>
      </div>
      <StatusPill level={tone} label={user.normalized_role.toUpperCase()} />
      {canChangeRole ? (
        <select
          aria-label={`Role for ${user.email}`}
          value={user.normalized_role}
          disabled={selectDisabled}
          onChange={(e) =>
            changeRole.mutate({ userId: user.user_id, role: e.target.value })
          }
          className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-meta text-gray-900 disabled:opacity-60"
        >
          {allowedRoles.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      ) : (
        <span />
      )}
      {canDeactivate ? (
        // user.status isn't on the generated OrgUser type yet (status
        // field was added under the org-users PR backlog); read it
        // defensively until that lands. Once it's typed, the cast
        // can drop.
        (user as OrgUser & { status?: string }).status === 'deactivated' ? (
          <TextButton
            label={activate.isPending || deactivate.isPending ? '…' : 'Activate'}
            onClick={() => activate.mutate({ userId: user.user_id })}
          />
        ) : (
          <TextButton
            label={deactivate.isPending || activate.isPending ? '…' : 'Deactivate'}
            onClick={() => deactivate.mutate({ userId: user.user_id })}
          />
        )
      ) : (
        <span />
      )}
    </li>
  );
}

function InvitationRow({ invite }: { invite: Invitation }) {
  return (
    <li className="grid grid-cols-[1fr_auto_auto] items-center gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2">
      <div className="min-w-0">
        <p className="truncate text-fs-body font-medium text-gray-900">{invite.email}</p>
        <p className="text-fs-meta text-gray-700">
          Invited as {invite.role} · expires{' '}
          <time dateTime={invite.expires_at}>
            {new Date(invite.expires_at).toLocaleDateString()}
          </time>
        </p>
      </div>
      <StatusPill level="watch" label="PENDING" />
      <span className="font-mono text-fs-meta text-gray-700">
        …{invite.token.slice(-8)}
      </span>
    </li>
  );
}

function InviteForm({
  clients,
  allowAdmin,
  onDone,
}: {
  clients: ApiClients;
  allowAdmin: boolean;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('maintainer');
  const [displayName, setDisplayName] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const invite = useInviteUser(clients);
  const groups = useGroups(clients);

  const roleOptions = allowAdmin
    ? [...ROLES_BELOW_ADMIN, 'admin']
    : ROLES_BELOW_ADMIN;

  const toggleGroup = (id: string) => {
    setGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );
  };

  return (
    <form
      aria-labelledby="invite-form-heading"
      className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      onSubmit={(e) => {
        e.preventDefault();
        invite.mutate(
          {
            email,
            role,
            displayName: displayName || undefined,
            groupIds: groupIds.length > 0 ? groupIds : undefined,
          },
          { onSuccess: () => onDone() },
        );
      }}
    >
      <h2
        id="invite-form-heading"
        className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
      >
        Invite a user
      </h2>
      <div className="mt-sp-3 grid grid-cols-1 gap-sp-3 sm:grid-cols-[2fr_1fr_1fr]">
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Display name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
          />
        </label>
      </div>

      {/* Group scope (PR-11). On accept, the new user is auto-added
          to each selected group. Hidden until the groups list loads
          so the form doesn't flash an empty fieldset. */}
      {groups.data && groups.data.length > 0 ? (
        <fieldset className="mt-sp-3 flex flex-col gap-sp-2">
          <legend className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Add to groups (optional)
          </legend>
          <ul className="flex flex-wrap gap-sp-3">
            {groups.data.map((g) => {
              const checked = groupIds.includes(g.id);
              return (
                <li key={g.id}>
                  <label className="inline-flex items-center gap-sp-2 text-fs-body text-gray-900">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGroup(g.id)}
                    />
                    {g.name}
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ) : null}

      <div className="mt-sp-3 flex items-center gap-sp-3">
        <PrimaryButton
          label={invite.isPending ? 'Sending…' : 'Send invitation'}
          type="submit"
          disabled={invite.isPending || !email}
        />
        <SecondaryButton label="Cancel" type="button" onClick={onDone} />
        {invite.isError ? (
          <span role="alert" className="text-fs-meta text-critical">
            Failed to invite. Check the email + role.
          </span>
        ) : null}
      </div>
    </form>
  );
}
